import React from 'react';

import { useApiLoading, useApiGet, req } from 'react-reqq-lite';
import dayjs from 'dayjs';

import { showDrawer } from 'modules/common/drawer/DrawerMarker';
import { showModal } from 'modules/common/modal';
import { formatCurrency, parseNumber } from 'modules/common/utils';
import { useGetTaxCharges } from 'modules/branch/services-management/settings/hooks';

import * as c from './constants';
import * as actions from './actions';
import OrderDetailsModal from './modals/OrderDetailsModal';

export const useGetDiscount = (defaultValue = {}) => {
  const discounts = useApiGet(c.GET_DISCOUNT_TYPES, defaultValue);

  const setDiscounts = React.useCallback(
    (payload) => {
      req.set(c.GET_DISCOUNT_TYPES, (state) =>
        typeof payload === 'function'
          ? payload(state[c.GET_DISCOUNT_TYPES] || defaultValue)
          : payload
      );
    },
    [defaultValue]
  );

  const discountPercent = discounts?.discount_type?.[0]?.value || 0;
  const included = {
    discountPercent,
    hasSelectedDiscount: discounts?.discount_type?.length > 0,
    discountValue: (subtotal, { withFormatCurrency = false } = {}) => {
      const v = discountPercent ? subtotal * (discountPercent / 100) : subtotal;

      if (withFormatCurrency) {
        return formatCurrency(v || 0);
      }

      return v;
    },
    discountedSubTotal: (subtotal) =>
      discountPercent
        ? subtotal - subtotal * (discountPercent / 100)
        : subtotal,
  };

  React.useEffect(
    () => () => {
      setDiscounts([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [discounts, setDiscounts, included];
};

export const useGetDiscountV2 = (defaultValue = {}) => {
  const discounts = useApiGet(c.GET_DISCOUNT_TYPES_V2, defaultValue);

  const setDiscounts = React.useCallback(
    (payload) => {
      req.set(c.GET_DISCOUNT_TYPES_V2, (state) =>
        typeof payload === 'function'
          ? payload(state[c.GET_DISCOUNT_TYPES_V2] || defaultValue)
          : payload
      );
    },
    [defaultValue]
  );

  const discountPercent = discounts?.discount_type?.[0]?.value || 0;
  const included = {
    discountPercent,
    hasSelectedDiscount: discounts?.discount_type?.length > 0,
    discountValue: (subtotal, { withFormatCurrency = false } = {}) => {
      const v = discountPercent ? subtotal * (discountPercent / 100) : subtotal;

      if (withFormatCurrency) {
        return formatCurrency(v || 0);
      }

      return v;
    },
    discountedSubTotal: (subtotal) =>
      discountPercent
        ? subtotal - subtotal * (discountPercent / 100)
        : subtotal,
  };

  React.useEffect(() => {
    setDiscounts(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValue)]);

  React.useEffect(
    () => () => {
      setDiscounts([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [discounts, setDiscounts, included];
};

export const useGetCharges = (defaultValue = []) => {
  const charges = useApiGet(c.GET_CHARGES, defaultValue);

  const setCharges = React.useCallback(
    (payload) => {
      req.set(c.GET_CHARGES, (state) =>
        typeof payload === 'function'
          ? payload(state[c.GET_CHARGES] || defaultValue)
          : payload
      );
    },
    [defaultValue]
  );

  const included = {};

  React.useEffect(() => {
    setCharges(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValue)]);

  React.useEffect(
    () => () => {
      setCharges([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [charges, setCharges, included];
};

export const useGetSettings = ({ disableAction = false } = {}) => {
  const [chargesList] = useGetCharges();
  const [isTaxLoading, taxList] = useGetTaxCharges({}, () => {}, {
    disableAction,
  });
  const getVAT = taxList?.find((item) => item?.key === 'value-added-tax');

  const totalCharge = chargesList?.reduce((acc, item) => acc + item?.value, 0);
  const totalVAT = getVAT?.value || 0;

  return {
    chargesList,
    totalCharge,
    totalVAT,
    isTaxLoading,
    taxList,
  };
};

export const useGetCart = (defaultValue = []) => {
  const cart = useApiGet(c.GET_CART, defaultValue);

  const setCart = React.useCallback((payload) => {
    req.set(c.GET_CART, (state) => [
      ...(typeof payload === 'function'
        ? payload(state[c.GET_CART] || [])
        : payload),
    ]);
  }, []);

  const productExistsCondition = (row, item, override) => {
    if (row?.has_variant && !override) {
      return (
        row?.uuid === item?.uuid &&
        row?.selected_variant?.uuid === item?.selected_variant?.uuid
      );
    }

    return row?.uuid === item?.uuid;
  };

  const productExistsIndex = (
    row,
    { override = false, isFilter = false } = {}
  ) => {
    let existsIndex = cart?.findIndex((item) =>
      productExistsCondition(row, item, override)
    );

    if (isFilter) {
      existsIndex = cart?.filter((item) =>
        productExistsCondition(row, item, override)
      );
    }

    return existsIndex;
  };

  const totalAddons = (row) =>
    (row?.selected_addons || [])?.reduce(
      (subAcc, subItem) => subAcc + parseNumber(subItem?.price),
      0
    );

  const cartRowTotal = (row, name) =>
    parseNumber(name ? row?.[name] : row?.price) * row?.quantity +
    totalAddons(row) * row?.quantity;

  const handleOnRemove = (index) => {
    setCart((prevState) => prevState?.filter((item, key) => key !== index));
  };

  const handleOnCreateUpdate = (value, { index = -1, type } = {}) => {
    const existsIndex = index > -1 ? index : productExistsIndex(value);

    if (existsIndex > -1 && type !== 'CREATE') {
      setCart((prevState) =>
        prevState
          ?.map((item, key) => {
            const $item = key === existsIndex ? value : item;

            if ($item?.quantity <= 0) return null;

            return $item;
          })
          ?.filter((item) => item)
      );
      return;
    }

    setCart((prevState) => [...prevState, value]);
  };

  const handleOnShowOrderDetails = (
    row,
    { withSku = false, type = 'CREATE', index = -1 } = {}
  ) => {
    const width = window.innerWidth;

    if (width > 640) {
      showDrawer({
        title: (
          <div>
            <div className="font-semibold">{row?.name}</div>
            {withSku && (
              <div className="font-normal uppercase text-sm text-gray-500">
                SKU: {withSku}
              </div>
            )}
          </div>
        ),
        content: (onClose) => (
          <OrderDetailsModal
            onClose={onClose}
            data={row}
            onChange={handleOnCreateUpdate}
            productExistsIndex={productExistsIndex}
            cart={cart}
            type={type}
            index={index}
          />
        ),
        align: 'left',
      });
      return;
    }

    showModal({
      title: <div className="font-semibold">{row?.name}</div>,
      content: (onClose) => (
        <OrderDetailsModal
          onClose={onClose}
          data={row}
          onChange={handleOnCreateUpdate}
          productExistsIndex={productExistsIndex}
          productExistsCondition={productExistsCondition}
          cart={cart}
        />
      ),
      withMobileFullscreen: true,
    });
  };

  const cartRowDiscountedPriceV2 = (value) => {
    // eslint-disable-next-line no-unused-vars
    const totalDiscount = value?.discount_type?.reduce(
      (acc, item) => acc + item?.value,
      0
    );

    // return cartRowTotal(value) - cartRowTotal(value) * (totalDiscount / 100);
    return cartRowTotal(value) - cartRowTotal(value);
  };

  const included = {
    cart,
    subtotal: (arr) =>
      (arr || cart)?.reduce((acc, item) => acc + cartRowTotal(item), 0),
    cartRowTotal,
    cartRowDiscountedPrice: cartRowDiscountedPriceV2,
    productExistsIndex,
    onCreateUpdate: handleOnCreateUpdate,
    onShowOrderDetails: handleOnShowOrderDetails,
    onRemove: handleOnRemove,
    productExistsCondition,
  };

  return [cart, setCart, included];
};

export const useGetDiscounts = (params, cb) => {
  const isApiLoading = useApiLoading(c.GET_DISCOUNTS, 'get');
  const data = useApiGet(`${c.GET_DISCOUNTS}.data`, []);
  const pagination = useApiGet(`${c.GET_DISCOUNTS}.pagination`, {});

  React.useEffect(() => {
    actions.getDiscounts(params, cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  return [isApiLoading, data, pagination];
};

export const useGetSelectedDiscount = (params, cb) => {
  const isApiLoading = useApiLoading(c.GET_SELECTED_DISCOUNT, 'get');
  const data = useApiGet(c.GET_SELECTED_DISCOUNT, []);

  React.useEffect(() => {
    actions.getSelectedDiscount(params, cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  return [isApiLoading, data];
};

export const usePostQuickCheckout = () => {
  const isApiLoading = useApiLoading(c.POST_QUICK_CHECKOUT, 'put');

  const action = (params, cb) => {
    actions.postQuickCheckout(params, cb);
  };

  return [isApiLoading, action];
};

export const usePutQuickCheckout = () => {
  const isApiLoading = useApiLoading(c.PUT_QUICK_CHECKOUT, 'put');

  const action = (params, cb) => {
    actions.putQuickCheckout(params, cb);
  };

  return [isApiLoading, action];
};

export const useGetBranchOpts = () => {
  const isApiLoading = useApiLoading(c.BRANCH_OPTIONS, 'get');
  const data = useApiGet(`${c.BRANCH_OPTIONS}.data`, []);

  const dateAsc = data?.sort(
    (a, b) =>
      dayjs(a?.attributes?.created_at).format('YYYYMMDD') -
      dayjs(b?.attributes?.created_at).format('YYYYMMDD')
  );

  const includes = {
    dateAsc,
  };

  return [isApiLoading, data, includes];
};

export const useCheckoutOrder = () => {
  const isApiLoading = useApiLoading(c.CHECKOUT_ORDER, 'post');

  const action = (params, cb) => {
    actions.checkoutOrder(params, cb);
  };

  return [isApiLoading, action];
};

export const useAddProduct = () => {
  const isApiLoading = useApiLoading(c.ADD_PRODUCT, 'post');

  const action = (params, cb) => {
    actions.addProduct(params, cb);
  };

  return [isApiLoading, action];
};
